import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { getUserProfile } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Skeleton } from '../components/ui/Skeleton';
import { User, Mail, Building2, Calendar, ArrowLeft, Edit, GraduationCap, UserCog } from 'lucide-react'; // Importando UserCog para o papel
import { Button } from '../components/ui/Button';
import Breadcrumb from '../components/Breadcrumb';
import EditProfileModal from '../components/EditProfileModal';

// Mapeamento para exibir os papéis de forma mais amigável
const roleMap = {
    ROLE_ADMIN: 'Administrador',
    ROLE_PARTICIPANTE: 'Participante',
};

// Componente auxiliar para exibir um item de detalhe
const DetailItem = ({ Icon, label, value }) => (
    <div className="flex items-start space-x-3 p-3 bg-muted/30 rounded-lg">
        <Icon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
        <div className="flex flex-col">
            <span className="text-xs font-medium text-muted-foreground uppercase">{label}</span>
            <p className="text-sm font-semibold text-foreground break-words">{value || 'N/A'}</p>
        </div>
    </div>
);

function ProfilePage() {
    const { isAuthenticated, logout } = useAuth();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showEditModal, setShowEditModal] = useState(false);

    const fetchProfile = useCallback(async () => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const data = await getUserProfile();
            setProfile(data);
        } catch (err) {
            console.error('Erro ao buscar perfil:', err);
            setError(err.message || 'Não foi possível carregar seu perfil.');
            toast.error(err.message || 'Erro ao carregar perfil.');
            if (err.message.includes('401') || err.message.includes('403')) {
                logout();
            }
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, navigate, logout]);

    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);

    const handleEditClick = () => {
        setShowEditModal(true);
    };

    const handleModalClose = () => {
        setShowEditModal(false);
    };

    const handleProfileUpdateSuccess = () => {
        setShowEditModal(false);
        fetchProfile(); // Recarrega o perfil para mostrar os dados atualizados
    };

    const breadcrumbItems = [
        { label: 'Dashboard', to: '/dashboard' },
        { label: 'Meu Perfil' }
    ];

    const ProfileSkeleton = () => (
        <Card className="w-full max-w-4xl mx-auto">
            <CardHeader>
                <Skeleton className="h-8 w-1/3 mb-2" />
                <Skeleton className="h-4 w-2/3" />
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
                {[...Array(5)].map((_, i) => (
                    <div key={i} className="p-3 bg-muted/30 rounded-lg">
                        <Skeleton className="h-4 w-1/4 mb-1" />
                        <Skeleton className="h-5 w-3/4" />
                    </div>
                ))}
            </CardContent>
        </Card>
    );

    if (loading) {
        return (
            <div className="container mx-auto p-4 md:p-6 lg:p-8">
                <Skeleton className="h-8 w-1/4 mb-4" />
                <ProfileSkeleton />
            </div>
        );
    }

    if (error) {
        return <div className="container mx-auto p-4 text-destructive">Erro: {error}</div>;
    }

    if (!profile) {
        return <div className="container mx-auto p-4 text-muted-foreground">Perfil não encontrado.</div>;
    }

    const userRole = profile.roles.map(role => roleMap[role] || role).join(', ');

    return (
        <div className="container mx-auto p-4 md:p-6 lg:p-8">
            <Breadcrumb items={breadcrumbItems} />
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 w-full md:w-auto">
                    <Button variant="outline" onClick={() => navigate('/dashboard')} className="w-full sm:w-auto">
                        <ArrowLeft className="mr-2 h-4 w-4" /> Voltar ao Dashboard
                    </Button>
                    <h1 className="text-3xl font-bold tracking-tight text-center sm:text-left w-full sm:w-auto">Meu Perfil</h1>
                </div>
                <Button onClick={handleEditClick} className="w-full md:w-auto">
                    <Edit className="mr-2 h-4 w-4" /> Editar Perfil
                </Button>
            </div>

            <Card className="w-full max-w-4xl mx-auto">
                <CardHeader className="border-b pb-4">
                    <CardTitle className="flex items-center text-2xl mb-1">
                        <User className="mr-3 h-6 w-6 text-primary" /> {profile.nome}
                    </CardTitle>
                    <CardDescription className="leading-relaxed flex items-center text-sm">
                        <UserCog className="mr-1.5 h-4 w-4" /> Papel: <span className="font-semibold ml-1 text-foreground">{userRole}</span>
                    </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                    <h3 className="text-lg font-semibold mb-4 text-primary">Detalhes da Conta</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Coluna 1: Informações de Contato */}
                        <div className="space-y-4">
                            <DetailItem 
                                Icon={Mail} 
                                label="Email" 
                                value={profile.email} 
                            />
                            <DetailItem 
                                Icon={Calendar} 
                                label="Membro Desde" 
                                value={profile.dataCadastro ? new Date(profile.dataCadastro).toLocaleDateString() : 'N/A'} 
                            />
                        </div>

                        {/* Coluna 2: Informações Acadêmicas/Profissionais */}
                        <div className="space-y-4">
                            <DetailItem 
                                Icon={GraduationCap} 
                                label="Grau Acadêmico" 
                                value={profile.grauAcademico} 
                            />
                            <DetailItem 
                                Icon={Building2} 
                                label="Instituição" 
                                value={profile.instituicao} 
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>

            {profile && (
                <EditProfileModal
                    isOpen={showEditModal}
                    onClose={handleModalClose}
                    initialData={profile}
                    onSuccess={handleProfileUpdateSuccess}
                />
            )}
        </div>
    );
}

export default ProfilePage;